import { Injectable } from '@angular/core';
import { Transition } from '@uirouter/core';

import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import {
  AccessLevel,
  DISPLAY_ACCESS,
  getEntityType,
} from '../types/access-level';
import { PermissionService } from './permission.service';
import { UserStateService } from './user-state.service';

@Injectable({
  providedIn: 'root',
})
export class AccessGuardService {
  private schedulesAccessRequestId = 0;

  constructor(
    private permissionService: PermissionService,
    private userStateService: UserStateService,
    private broadcasterService: BroadcasterService,
  ) {
    this.broadcasterService.on(
      'risevision.company.selectedCompanyChanged',
      () => {
        this.schedulesAccessRequestId++;
      },
    );
  }

  requireAccess(level: AccessLevel, transition: Transition): Promise<unknown> {
    if (this.permissionService.hasAccess(level)) {
      return Promise.resolve();
    }

    const entityId = this.getTransitionEntityId(transition);
    const entityType = getEntityType(level);

    if (!entityId || entityType === null) {
      return this.redirectUnauthorized(transition);
    }

    return this.permissionService.loadShareAccess(entityType, entityId).then(
      () => {
        if (this.permissionService.hasAccess(level, entityId)) {
          return;
        }

        return this.redirectUnauthorized(transition);
      },
      () => this.redirectUnauthorized(transition),
    );
  }

  requireSharedOrRoleAccess(
    level: AccessLevel,
    transition: Transition,
  ): Promise<unknown> {
    if (this.permissionService.hasAccess(level)) {
      return Promise.resolve();
    }

    return this.permissionService.hasAnySharedAccess(level).then(
      (hasSharedAccess) => {
        if (hasSharedAccess) {
          return;
        }

        return this.redirectUnauthorized(transition);
      },
      () => this.redirectUnauthorized(transition),
    );
  }

  requireDisplaysAccess(transition: Transition): Promise<unknown> {
    if (this.permissionService.canBrowseDisplays()) {
      return Promise.resolve();
    }

    return this.requireSharedOrRoleAccess(
      DISPLAY_ACCESS.FULL_ACCESS,
      transition,
    );
  }

  requireScreenSharingAccess(transition: Transition): Promise<unknown> {
    return this.permissionService.canAccessScreenSharing().then(
      (allowed) => {
        if (allowed) {
          return;
        }

        return this.redirectUnauthorized(transition);
      },
      () => this.redirectUnauthorized(transition),
    );
  }

  requireScreenSharingRoomAccess(transition: Transition): Promise<unknown> {
    if (this.permissionService.canBrowseScreenSharing()) {
      return Promise.resolve();
    }

    return this.requireAccess(
      DISPLAY_ACCESS.SCREEN_SHARING_MODERATOR,
      transition,
    );
  }

  requireSchedulesAccess(transition: Transition): Promise<unknown> {
    return this.requireSchedulesLibraryAccess(transition);
  }

  requireAdminAccess(transition: Transition): Promise<unknown> {
    if (this.userStateService.hasPermission('sa ua')) {
      return Promise.resolve();
    }

    return this.redirectUnauthorized(transition);
  }

  requireBillingAccess(transition: Transition): Promise<unknown> {
    if (this.userStateService.canAccessBilling()) {
      return Promise.resolve();
    }

    return this.redirectUnauthorized(transition);
  }

  requireAdminOrOwnUserAccess(transition: Transition): Promise<unknown> {
    const name = transition.to().name ?? '';

    if (name === 'apps.user.details' || name.startsWith('apps.user.details.')) {
      const username = transition.params().username as string | undefined;

      if (!username || this.userStateService.checkUsername(username)) {
        return Promise.resolve();
      }
    }

    return this.requireAdminAccess(transition);
  }

  private getTransitionEntityId(transition: Transition): string {
    const params = transition.params();
    const presentationId = params.presentationId as string | undefined;

    if (presentationId && presentationId !== 'new') {
      return presentationId;
    }

    return (
      (params.displayId as string | undefined) ||
      (params.scheduleId as string | undefined) ||
      ''
    );
  }

  private requireSchedulesLibraryAccess(
    transition: Transition,
  ): Promise<unknown> {
    const requestId = ++this.schedulesAccessRequestId;

    return this.permissionService.canAccessSchedules().then(
      (allowed) => {
        if (requestId !== this.schedulesAccessRequestId) {
          return this.rejectStaleSchedulesAccess(transition);
        }

        if (allowed) {
          return;
        }

        return this.redirectUnauthorized(transition);
      },
      () => {
        if (requestId !== this.schedulesAccessRequestId) {
          return this.rejectStaleSchedulesAccess(transition);
        }

        return this.redirectUnauthorized(transition);
      },
    );
  }

  private rejectStaleSchedulesAccess(transition: Transition): Promise<unknown> {
    transition.abort();
    return Promise.reject(new Error('Stale schedules access check'));
  }

  private redirectUnauthorized(transition: Transition): Promise<unknown> {
    transition.abort();
    return transition.router.stateService.go(
      this.permissionService.getHomeState(),
    );
  }
}
